/* ==========================================================================
   ENDURRAD — Ricerca intelligente sul sito (fase 2)
   File: assets/endurrad-smart-search.js
   --------------------------------------------------------------------------
   Usa il motore di ricerca del server Endurrad (GET /api/search, lo stesso
   dell'app iOS), che conosce tutti i prodotti, le moto e i sinonimi.

   1. Tendina mentre scrivi (barra Endurrad): in cima alla tendina nativa
      compaiono categoria e moto riconosciute, i prodotti giusti e
      «Forse cercavi…» se c'è un errore di battitura.
   2. Invio (CERCA): apre /search con i filtri Shopify già attivi
      (tipologia, marchio, marca e modello moto, prezzo); il testo con
      refusi viene corretto prima dell'invio.
   3. Zero risultati: mostra i prodotti trovati dal server, «Forse cercavi…»,
      la stessa categoria per tutte le moto e il link ai contatti.
   4. Ritocchi: campo e titolo della pagina senza «*», pillola della moto
      sempre visibile tra i filtri attivi.

   Sicurezza e ripiego:
   - se il server non risponde entro 1,5 s o dà errore, la ricerca parte
     normale, esattamente come prima; la tendina nativa resta com'è;
   - il testo cercato non viene salvato dal server;
   - nessun file del tema Concept viene modificato: lo script si carica
     dallo snippet global-search-endurrad.liquid.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__endurradSmartSearch) return;
  window.__endurradSmartSearch = true;

  var API = 'https://endurrad-api.endurrad.workers.dev/api/search';
  var TIMEOUT_MS = 1500;        /* attesa massima quando il cliente preme CERCA */
  var FETCH_TIMEOUT_MS = 5000;  /* tendina: il server al primo avvio può metterci qualche secondo */
  var MIN_LENGTH = 3;
  var DEBOUNCE_MS = 250;
  var LIMIT = 12;
  var DROPDOWN_ITEMS = 4;
  var EMPTY_ITEMS = 8;

  /* ---------------- Testi (lingua della pagina) ---------------- */

  var TEXTS = {
    it: {
      didYouMean: 'Forse cercavi',
      seeAll: 'Vedi tutti i risultati',
      found: 'Prodotti adatti alla tua ricerca',
      noBike: 'Non abbiamo prodotti specifici per {bike}.',
      noBikeLink: 'Vedi la stessa categoria per tutte le moto',
      noBikeNotice: 'Nessun prodotto specifico per {bike}: ti mostriamo quelli per tutte le moto. Controlla la compatibilità nella scheda prodotto.',
      help: 'Non trovi quello che cerchi? Scrivici: ti aiutiamo a trovare l’accessorio giusto.',
      contact: 'Contattaci',
      remove: 'Rimuovi'
    },
    en: {
      didYouMean: 'Did you mean',
      seeAll: 'See all results',
      found: 'Products matching your search',
      noBike: 'We have no products specific to the {bike}.',
      noBikeLink: 'See the same category for all motorcycles',
      noBikeNotice: 'No products specific to the {bike}: showing those for all motorcycles. Check compatibility on the product page.',
      help: 'Can’t find what you’re looking for? Write to us and we’ll help you find the right accessory.',
      contact: 'Contact us',
      remove: 'Remove'
    },
    de: {
      didYouMean: 'Meinten Sie',
      seeAll: 'Alle Ergebnisse anzeigen',
      found: 'Passende Produkte zu Ihrer Suche',
      noBike: 'Wir haben keine speziellen Produkte für die {bike}.',
      noBikeLink: 'Dieselbe Kategorie für alle Motorräder anzeigen',
      noBikeNotice: 'Keine speziellen Produkte für die {bike}: Wir zeigen die für alle Motorräder. Prüfen Sie die Kompatibilität auf der Produktseite.',
      help: 'Nicht gefunden, was Sie suchen? Schreiben Sie uns, wir helfen Ihnen gern.',
      contact: 'Kontakt',
      remove: 'Entfernen'
    },
    fr: {
      didYouMean: 'Vouliez-vous dire',
      seeAll: 'Voir tous les résultats',
      found: 'Produits correspondant à votre recherche',
      noBike: 'Nous n’avons pas de produits spécifiques pour la {bike}.',
      noBikeLink: 'Voir la même catégorie pour toutes les motos',
      noBikeNotice: 'Aucun produit spécifique pour la {bike} : voici ceux pour toutes les motos. Vérifiez la compatibilité sur la fiche produit.',
      help: 'Vous ne trouvez pas ce que vous cherchez ? Écrivez-nous, nous vous aiderons.',
      contact: 'Nous contacter',
      remove: 'Supprimer'
    },
    es: {
      didYouMean: 'Quizás buscabas',
      seeAll: 'Ver todos los resultados',
      found: 'Productos que coinciden con tu búsqueda',
      noBike: 'No tenemos productos específicos para la {bike}.',
      noBikeLink: 'Ver la misma categoría para todas las motos',
      noBikeNotice: 'No hay productos específicos para la {bike}: te mostramos los de todas las motos. Comprueba la compatibilidad en la ficha del producto.',
      help: '¿No encuentras lo que buscas? Escríbenos y te ayudamos a encontrar el accesorio adecuado.',
      contact: 'Contáctanos',
      remove: 'Eliminar'
    }
  };
  var lang = (document.documentElement.lang || 'it').slice(0, 2).toLowerCase();
  var T = TEXTS[lang] || TEXTS.it;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

  /* ---------------- Server ---------------- */

  var cache = {};
  var cacheKeys = [];

  function api(q) {
    if (cache[q]) return cache[q];
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () {
      if (controller) controller.abort();
    }, FETCH_TIMEOUT_MS);

    var promise = fetch(API + '?q=' + encodeURIComponent(q) + '&limit=' + LIMIT, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      })
      .finally(function () {
        clearTimeout(timer);
      });

    /* In cache solo le risposte valide (un errore si riprova al giro dopo). */
    cache[q] = promise;
    cacheKeys.push(q);
    if (cacheKeys.length > 20) delete cache[cacheKeys.shift()];
    promise.then(function (data) {
      if (!data) delete cache[q];
    });
    return promise;
  }

  /* Come api(), ma dopo `ms` risponde null (ricerca normale) senza aspettare oltre. */
  function apiWithin(q, ms) {
    return Promise.race([
      api(q),
      new Promise(function (resolve) {
        setTimeout(function () { resolve(null); }, ms);
      })
    ]);
  }

  /* Risveglia il server appena il cliente tocca la barra: la prima ricerca
     vera arriva così a server già pronto. Una volta sola per pagina. */
  var warmed = false;
  function warmUp() {
    if (warmed) return;
    warmed = true;
    api('accessori moto');
  }

  /* ---------------- URL ---------------- */

  /* Solo i form GET verso /search (anche /en/search, /de/search, ...) dello stesso dominio. */
  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if ((form.getAttribute('method') || 'get').toLowerCase() !== 'get') return false;
    if (!form.querySelector('input[name="q"]')) return false;
    try {
      var url = new URL(form.getAttribute('action') || '', window.location.href);
      return url.origin === window.location.origin && /\/search\/?$/.test(url.pathname);
    } catch (e) {
      return false;
    }
  }

  function searchAction(form) {
    return (form && form.getAttribute('action')) || ROOT + 'search';
  }

  /* Filtri utili sul sito: tipologia, marchio, moto scritta nel testo, prezzo.
     «La mia moto» (garage) esiste solo nell'app: qui viene ignorata. */
  function isBike(b) {
    return !!b && typeof b === 'object' && !!b.brand && !!b.model;
  }

  function hasSiteFilters(it) {
    if (!it || !Array.isArray(it.chips) || it.chips.length === 0) return false;
    return (
      (Array.isArray(it.productTypes) && it.productTypes.length > 0) ||
      !!it.vendor ||
      isBike(it.bike) ||
      (!!it.price && (typeof it.price.min === 'number' || typeof it.price.max === 'number'))
    );
  }

  function bikeLabel(it) {
    return isBike(it.bike) ? it.bike.brand + ' ' + it.bike.model : '';
  }

  function filteredUrl(action, it, opts) {
    opts = opts || {};
    var url = new URL(action, window.location.href);
    var p = url.searchParams;
    var rest = typeof it.query === 'string' ? it.query.trim() : '';

    p.set('q', rest || '*');
    p.set('type', 'product');
    p.set('options[prefix]', 'last');

    (it.productTypes || []).forEach(function (t) {
      p.append('filter.p.product_type', t);
    });
    if (it.vendor) p.append('filter.p.vendor', it.vendor);
    if (isBike(it.bike) && !opts.noBike) {
      p.append('filter.p.m.filtro.marca_moto', it.bike.brand);
      p.append('filter.p.m.filtro.modello_moto', it.bike.model);
    }
    if (it.price) {
      if (typeof it.price.min === 'number') p.set('filter.v.price.gte', String(it.price.min));
      if (typeof it.price.max === 'number') p.set('filter.v.price.lte', String(it.price.max));
    }
    /* Parametri Endurrad (ignorati da Shopify): testo originale e moto tolta. */
    if (opts.original) p.set('erd_q', opts.original);
    if (opts.noBike && isBike(it.bike)) p.set('erd_nobike', bikeLabel(it));
    return url.toString();
  }

  function plainUrl(action, text) {
    var url = new URL(action, window.location.href);
    url.searchParams.set('q', text);
    url.searchParams.set('options[prefix]', 'last');
    return url.toString();
  }

  function productUrl(handle) {
    return ROOT + 'products/' + encodeURIComponent(handle);
  }

  /* Dove portare il cliente che preme CERCA (null = ricerca normale). */
  function destination(action, q, data) {
    if (!data || !data.interpret) return null;
    var it = data.interpret;
    if (hasSiteFilters(it)) {
      if (data.total === 0 && data.withoutBike > 0 && isBike(it.bike)) {
        return filteredUrl(action, it, { noBike: true, original: q });
      }
      return filteredUrl(action, it, { original: q });
    }
    if (data.corrected) return plainUrl(action, data.corrected);
    return null;
  }

  /* ---------------- DOM ---------------- */

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function link(href, cls, text) {
    var a = el('a', cls, text);
    a.href = href;
    return a;
  }

  function price(value) {
    if (typeof value !== 'number' || CURRENCY !== 'EUR') return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: 'EUR' }).format(value);
    } catch (e) {
      return '€ ' + value;
    }
  }

  function thumb(src) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160';
  }

  function productList(results, max) {
    var ul = el('ul', 'erd-smart__list');
    results.slice(0, max).forEach(function (r) {
      var li = el('li');
      var a = link(productUrl(r.handle), 'erd-smart__item');
      var media = el('span', 'erd-smart__media');
      if (r.image) {
        var img = el('img');
        img.src = thumb(r.image);
        img.alt = '';
        img.loading = 'lazy';
        img.width = 64;
        img.height = 64;
        media.appendChild(img);
      }
      var body = el('span', 'erd-smart__body');
      body.appendChild(el('span', 'erd-smart__title', r.title));
      var meta = [r.vendor, price(r.price)].filter(Boolean).join(' · ');
      if (meta) body.appendChild(el('span', 'erd-smart__meta', meta));
      a.appendChild(media);
      a.appendChild(body);
      li.appendChild(a);
      ul.appendChild(li);
    });
    return ul;
  }

  /* «Forse cercavi…»: porta alla frase corretta già filtrata (categoria, moto…),
     non alla ricerca di testo di Shopify. */
  function correctionLine(action, data) {
    var it = data.interpret;
    var href = hasSiteFilters(it) ? filteredUrl(action, it, { original: data.corrected }) : plainUrl(action, data.corrected);
    var p = el('p', 'erd-smart__fix');
    p.appendChild(document.createTextNode(T.didYouMean + ' '));
    p.appendChild(link(href, 'erd-smart__fix-link', '«' + data.corrected + '»'));
    p.appendChild(document.createTextNode('?'));
    return p;
  }

  /* ---------------- 1. Tendina mentre scrivi ---------------- */

  function dropdownBlock(form, q, data) {
    if (!data || !data.interpret) return null;
    var it = data.interpret;
    var chips = (it.chips || []).filter(function (c) {
      return c.key !== 'bike' || isBike(it.bike);
    });
    var codeHit = data.results && data.results[0] && data.results[0].match === 'codice';
    var action = searchAction(form);
    var box = el('div', 'erd-smart');

    if (data.corrected) box.appendChild(correctionLine(action, data));

    if (data.total > 0 && (chips.length > 0 || data.corrected || codeHit)) {
      var head = link(hasSiteFilters(it) ? filteredUrl(action, it, { original: q }) : plainUrl(action, data.corrected || q), 'erd-smart__head');
      if (chips.length > 0) {
        head.appendChild(el('span', 'erd-smart__chips', chips.map(function (c) { return c.label; }).join(' · ')));
      }
      head.appendChild(el('span', 'erd-smart__all', T.seeAll + ' →'));
      box.appendChild(head);
      box.appendChild(productList(data.results || [], DROPDOWN_ITEMS));
    } else if (data.total === 0 && data.withoutBike > 0 && isBike(it.bike) && hasSiteFilters(it)) {
      box.appendChild(el('p', 'erd-smart__note', T.noBike.replace('{bike}', bikeLabel(it))));
      box.appendChild(link(filteredUrl(action, it, { noBike: true, original: q }), 'erd-smart__head', T.noBikeLink + ' →'));
    }

    return box.childNodes.length > 0 ? box : null;
  }

  function setupDropdown(form) {
    var input = form.querySelector('input[name="q"]');
    var target = form.querySelector('[data-endurrad-results]');
    if (!input || !target || form.__erdSmart) return;
    form.__erdSmart = true;

    var state = { q: '', node: null };
    var timer = null;

    /* La tendina nativa sostituisce il contenuto a ogni risposta: il blocco
       Endurrad viene rimesso in cima finché il testo è lo stesso. */
    function place() {
      var current = target.querySelector(':scope > .erd-smart');
      var valid = state.node && input.value.trim() === state.q;
      if (!valid) {
        if (current) current.remove();
        return;
      }
      if (target.firstChild === state.node) return;
      if (current && current !== state.node) current.remove();
      target.insertBefore(state.node, target.firstChild);
    }

    new MutationObserver(place).observe(target, { childList: true });

    input.addEventListener('focus', warmUp);
    input.addEventListener('pointerdown', warmUp);

    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < MIN_LENGTH) {
        state.node = null;
        place();
        return;
      }
      timer = setTimeout(function () {
        api(q).then(function (data) {
          if (input.value.trim() !== q) return;
          state.q = q;
          state.node = dropdownBlock(form, q, data);
          place();
        });
      }, DEBOUNCE_MS);
    });

    form.addEventListener('reset', function () {
      state.node = null;
      place();
    });
  }

  /* ---------------- 2. Invio ---------------- */

  /* Invio normale del form: submit() non rilancia l'evento, quindi non ripassa da qui. */
  function submitNormally(form) {
    HTMLFormElement.prototype.submit.call(form);
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (event.defaultPrevented || !isSearchForm(form)) return;

      var input = form.querySelector('input[name="q"]');
      var q = (input.value || '').trim();
      if (q.length < MIN_LENGTH || q === '*') return;

      event.preventDefault();
      form.setAttribute('loading', '');

      apiWithin(q, TIMEOUT_MS).then(function (data) {
        form.removeAttribute('loading');
        var url = destination(searchAction(form), q, data);
        if (url) window.location.assign(url);
        else submitNormally(form);
      });
    },
    true
  );

  /* ---------------- 3 e 4. Pagina dei risultati ---------------- */

  function onSearchPage() {
    return /\/search\/?$/.test(window.location.pathname);
  }

  function pageParams() {
    try {
      return new URL(window.location.href).searchParams;
    } catch (e) {
      return new URLSearchParams();
    }
  }

  /* Campo vuoto invece di «*» (solo i campi visibili: i campi nascosti dei
     filtri del tema devono tenere q=* per funzionare) e titolo senza «*». */
  function hideStar() {
    if (pageParams().get('q') !== '*') return;
    document.querySelectorAll('input[type="search"][name="q"]').forEach(function (input) {
      if (input.value === '*') input.value = '';
    });
    document.title = document.title.replace(/\s+\S+\s+["«“„]\*["»”“]/, '');
  }

  /* Pillola della moto: se il tema non la mostra tra i filtri attivi, la aggiunge. */
  var BIKE_PARAMS = ['filter.p.m.filtro.marca_moto', 'filter.p.m.filtro.modello_moto'];

  function ensureBikePills() {
    var box = document.getElementById('FacetFiltersActive');
    if (!box) return;
    var params = pageParams();
    var titles = Array.prototype.map.call(box.querySelectorAll('.removable-facet .facet-title'), function (t) {
      return t.textContent.trim().toLowerCase();
    });
    var icon = box.querySelector('.removable-facet a .btn-text');

    BIKE_PARAMS.forEach(function (name) {
      params.getAll(name).forEach(function (value) {
        if (titles.indexOf(value.trim().toLowerCase()) !== -1) return;
        if (box.querySelector('.erd-pill[data-value="' + CSS.escape(name + '=' + value) + '"]')) return;

        var url = new URL(window.location.href);
        var keep = url.searchParams.getAll(name).filter(function (v) { return v !== value; });
        url.searchParams.delete(name);
        keep.forEach(function (v) { url.searchParams.append(name, v); });
        url.searchParams.delete('page');

        var pill = el('div', 'removable-facet erd-pill flex items-center justify-between gap-3 lg:gap-4 text-xs lg:text-sm leading-none');
        pill.setAttribute('data-value', name + '=' + value);
        pill.appendChild(el('div', 'facet-title flex items-center gap-2', value));
        var a = link(url.toString(), 'block relative');
        a.setAttribute('aria-label', T.remove + ' ' + value);
        if (icon) a.appendChild(icon.cloneNode(true));
        else a.appendChild(el('span', 'btn-text', '×'));
        pill.appendChild(a);
        box.insertBefore(pill, box.querySelector('.facet-clear-all'));
      });
    });
  }

  /* Moto tolta perché senza prodotti specifici: avviso in cima ai risultati. */
  function noBikeNotice() {
    var bike = pageParams().get('erd_nobike');
    var grid = document.getElementById('ProductGridContainer');
    if (!bike || !grid || grid.querySelector('.erd-smart-notice')) return;
    grid.insertBefore(el('p', 'erd-smart-notice', T.noBikeNotice.replace('{bike}', bike)), grid.firstChild);
  }

  /* Zero risultati: prodotti trovati dal server, «Forse cercavi…», contatti. */
  function enhanceEmptyState() {
    var empty = document.querySelector('#ProductGridContainer .empty-state');
    if (!empty || empty.__erdSmart) return;
    empty.__erdSmart = true;

    var params = pageParams();
    var q = (params.get('erd_q') || params.get('q') || '').trim();
    var action = ROOT + 'search';
    var box = el('div', 'erd-smart erd-smart--page');

    function finish() {
      var help = el('p', 'erd-smart__help', T.help + ' ');
      help.appendChild(link(ROOT + 'pages/contatti', 'erd-smart__fix-link', T.contact));
      box.appendChild(help);
      empty.appendChild(box);
    }

    if (q.length < MIN_LENGTH || q === '*') return finish();

    api(q).then(function (data) {
      if (data && data.interpret) {
        var it = data.interpret;
        if (data.corrected) box.appendChild(correctionLine(action, data));
        if (data.total > 0) {
          box.appendChild(el('p', 'erd-smart__chips', T.found));
          box.appendChild(productList(data.results || [], EMPTY_ITEMS));
        } else if (data.withoutBike > 0 && isBike(it.bike) && hasSiteFilters(it)) {
          box.appendChild(el('p', 'erd-smart__note', T.noBike.replace('{bike}', bikeLabel(it))));
          box.appendChild(link(filteredUrl(action, it, { noBike: true, original: q }), 'erd-smart__head', T.noBikeLink + ' →'));
        }
      }
      finish();
    });
  }

  /* Pagina /search aperta con una frase ma senza filtri (link «Vedi di più» della
     tendina nativa, cassetto di ricerca, vecchi link, pulsante Indietro):
     se il server riconosce categoria, moto, marchio o prezzo si passa subito alla
     stessa pagina con i filtri giusti. Mai in loop: con un filtro attivo non si tocca. */
  function upgradePlainSearch() {
    var params = pageParams();
    var q = (params.get('q') || '').trim();
    if (q.length < MIN_LENGTH || q === '*' || params.has('erd_q')) return;
    var hasFilter = false;
    params.forEach(function (v, k) { if (k.indexOf('filter.') === 0) hasFilter = true; });
    if (hasFilter) return;

    var root = document.documentElement;
    root.classList.add('erd-checking');
    apiWithin(q, TIMEOUT_MS).then(function (data) {
      var url = destination(window.location.pathname, q, data);
      if (url && data && hasSiteFilters(data.interpret)) {
        window.location.replace(url);
        return;
      }
      root.classList.remove('erd-checking');
    });
  }

  function enhancePage() {
    hideStar();
    ensureBikePills();
    noBikeNotice();
    enhanceEmptyState();
  }

  /* ---------------- Stile ---------------- */

  var CSS_TEXT = [
    '.erd-smart{display:grid;gap:12px;width:100%;margin:0 0 20px;padding:0 0 18px;border-bottom:1px solid rgba(0,0,0,.08);text-align:left}',
    '.erd-smart--page{max-width:640px;margin:24px auto 0;padding:0;border:0}',
    '.erd-smart__fix,.erd-smart__note,.erd-smart__help{margin:0;font-size:14px;line-height:1.4;color:rgba(0,0,0,.7)}',
    '.erd-smart__fix-link{font-weight:600;color:#086CE5;text-decoration:underline}',
    '.erd-smart__head{display:flex;flex-wrap:wrap;align-items:baseline;justify-content:space-between;gap:6px 16px;text-decoration:none;color:inherit}',
    '.erd-smart__chips{font-size:12px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;margin:0}',
    '.erd-smart__all{font-size:13px;font-weight:600;color:#086CE5}',
    '.erd-smart__list{list-style:none;margin:0;padding:0;display:grid;gap:10px}',
    '.erd-smart__item{display:flex;align-items:center;gap:14px;text-decoration:none;color:inherit;border-radius:12px;padding:4px}',
    '.erd-smart__item:hover,.erd-smart__item:focus-visible{background:rgba(8,108,229,.06)}',
    '.erd-smart__media{flex:0 0 64px;width:64px;height:64px;border-radius:10px;overflow:hidden;background:#f4f4f4;display:flex;align-items:center;justify-content:center}',
    '.erd-smart__media img{width:100%;height:100%;object-fit:contain}',
    '.erd-smart__body{display:grid;gap:4px;min-width:0}',
    '.erd-smart__title{font-size:15px;font-weight:600;line-height:1.3;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}',
    '.erd-smart__meta{font-size:13px;color:rgba(0,0,0,.6)}',
    '.erd-smart ~ .search__empty{display:none}',
    '.erd-checking #ProductGridContainer{opacity:.25;transition:opacity .15s}',
    '.erd-smart-notice{margin:0 0 16px;padding:12px 16px;border-radius:12px;background:rgba(8,108,229,.08);font-size:14px;line-height:1.4}'
  ].join('\n');

  function addStyle() {
    if (document.getElementById('erd-smart-style')) return;
    var style = el('style');
    style.id = 'erd-smart-style';
    style.textContent = CSS_TEXT;
    document.head.appendChild(style);
  }

  /* ---------------- Avvio ---------------- */

  function start() {
    addStyle();
    document.querySelectorAll('form.endurrad-search-form').forEach(setupDropdown);

    if (onSearchPage()) {
      upgradePlainSearch();
      enhancePage();
      /* I filtri del tema si aggiornano senza ricaricare la pagina. */
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          enhancePage();
        });
      }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
